import { useEffect, useState } from 'react'
import { fetchContentFulData } from '@helpers/contentful'
import trackError from '@helpers/trackError'
import { TROUSERS, TOPS, SHORTS } from '@consts/index'
import { getProductCategory } from '@helpers/products'

type ImageCard = {
  title: string
  file: {
    url: string
    details: {
      size: number
      image: {
        width: number
        height: number
      }
    }
    fileName: string
    contentType: string
  }
}

export type SizingWidgetItem = {
  title: string
  description: string
  imageMobileHero: ImageCard
  imageCard: ImageCard
  imageDesktopHero: ImageCard
  type: string
}

export type SizingWidgetBlock = {
  productType: string
  sizingItems: Record<string, SizingWidgetItem>
  defaultBgColor: string
  hoverBgColor: string
  transitionBgColor: string
  byline: string
  type: string
}

type SizingWidgetBlocks = Record<string, SizingWidgetBlock>

export type SizingWidgetData = {
  title: string
  byline: string
  linkText?: string
  linkUrl?: string
  sizingBlocks: SizingWidgetBlocks
  excludeProductTypes: ArrayLike<string> | null
}

const getItemCategory = (productCategory: string | undefined) => {
  switch (true) {
    case productCategory?.includes(SHORTS):
      return SHORTS

    case productCategory?.includes(TOPS):
      return TOPS

    case productCategory?.includes('trouser'):
    case productCategory?.includes('denim'):
      return TROUSERS

    default:
      return null
  }
}

const hasImages = (
  sizingBlocks: SizingWidgetBlocks | undefined = {},
  handle: string,
  productCategory: string | undefined
) => {
  let imageExists = true
  const allData = Object.values(sizingBlocks).map(item =>
    Object.values(item.sizingItems)
  )
  allData
    .flatMap(data => data)
    .forEach(value => {
      if (
        !value.imageDesktopHero.file.url ||
        !value.imageMobileHero.file.url ||
        !value.imageCard.file.url
      ) {
        imageExists = false
        trackError(new Error('Could not load sizing widget'), {
          handle,
          productCategory
        })
      }
    })
  return imageExists
}

const hasCategory = (
  sizingBlocks: SizingWidgetBlocks = {},
  category: string | undefined
) => {
  const categoryInData = Object.values(sizingBlocks).find(
    item => item.productType.toLowerCase() === category?.toLowerCase()
  )

  return !!categoryInData
}

type UseSizingWidgetArgs = {
  productOptions?: { type: string }[]
  productType?: string
  productCategory?: string
  handle: string
  isPreview: boolean
}

export default function useSizingWidgetData({
  productOptions = [],
  productType,
  productCategory,
  handle,
  isPreview
}: UseSizingWidgetArgs) {
  const [sizingWidgetData, setSizingWidgetData] =
    useState<SizingWidgetData | null>(null)

  useEffect(() => {
    const fetchSizingWidgetData = async () => {
      try {
        const data = (await fetchContentFulData({
          id: '3Y2dy1zyiarkO6N2jADqra',
          depth: 10,
          unNest: false,
          isPreview
        })) as SizingWidgetData

        setSizingWidgetData(data)
      } catch (error) {
        trackError(new Error('Sizing Widget failed'), { error: error as Error })
      }
    }
    fetchSizingWidgetData()
  }, [isPreview])

  const excludedProductTypes = Object.values(
    sizingWidgetData?.excludeProductTypes || {}
  ).map(value => value.toLocaleLowerCase())

  const isProductTypeExcluded = excludedProductTypes.includes(
    productType?.toLowerCase() || ''
  )

  const category =
    getItemCategory(productCategory) ||
    getProductCategory(productOptions, productType)?.toLowerCase()

  return !isProductTypeExcluded &&
    hasCategory(sizingWidgetData?.sizingBlocks, category) &&
    hasImages(sizingWidgetData?.sizingBlocks, handle, productCategory)
    ? { sizingWidgetData, sizingWidgetCategory: category }
    : {}
}
